import qs from 'qs'

import type { definitions, paths } from '@luxuryescapes/contract-svc-accommodation'

import request from 'api/requestUtils'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import { mapObject } from 'lib/object/objectUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

import propertyRateMap from './mappers/propertyRateMap'

interface PropertyRatesParams {
  checkIn: string;
  checkOut: string;
  occupancies: Array<App.Occupants>;
  regionCode: string;
  currency: string;
  previewMode: boolean;
  dynamicAvailability: boolean;
  utmContent: string | undefined;
  roomTypeId: string | undefined;
  roomRateId: string | undefined;
}

interface PropertyLocationParams {
  regionCode: string;
  currency: string;
}

type PropertyLocation = { latitude: number, longitude: number }

// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerUnifiedAvailabilityRequestQueryParams = Omit<paths['/api/accommodation/properties/availability']['get']['parameters']['query'], 'brand'>
type ServerUnifiedAvailabilityResponse = paths['/api/accommodation/properties/availability']['get']['responses']['200']['content']['application/json']
type ServerAvailabilityResponseProperty = definitions['AvailabilityResponseProperty']
type ServerPropertyMappingRequestQueryParams = paths['/api/accommodation/properties/mappings']['get']['parameters']['query']
type ServerPropertyMappingResponse = paths['/api/accommodation/properties/mappings']['get']['responses']['200']['content']['application/json']
type ServerPropertyContentRequestQueryParams = Omit<paths['/api/accommodation/content/properties/{propertyId}']['get']['parameters']['query'], 'brand'>
type ServerPropertyContentResponse = paths['/api/accommodation/content/properties/{propertyId}']['get']['responses']['200']['content']['application/json']

const GHA_ROOM_RATE_ID_PATTERN = /^(.+?)(?:_([A-Z]{3}))?$/

function parseGhaRoomRateId(roomRateId: string | undefined): [string | undefined, string | undefined] {
  if (!roomRateId) return [undefined, undefined]
  const result = GHA_ROOM_RATE_ID_PATTERN.exec(roomRateId)
  if (!result) return [undefined, undefined]
  return [result[1], result[2]]
}

function mapHighlightedReason(
  utmContent: string | undefined,
): ServerUnifiedAvailabilityRequestQueryParams['highlightedReason'] {
  switch (utmContent) {
    case 'gha-lpc':
    case 'gha-lpp':
      return utmContent
  }
}

async function getPropertyMappingId(
  queryParams: ServerPropertyMappingRequestQueryParams,
): Promise<string | undefined> {
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerPropertyMappingResponse>(
    `/api/accommodation/properties/mappings?${query}`,
    { excludeBrand: true },
  )
  return response.result?.id
}

async function getMappedPropertyId(
  propertyId: string,
): Promise<string | undefined> {
  // Search property ids can be source ids; mapping normalises them to public accommodation ids.
  // A `bedbank:` search id is sometimes the mapping's own id and sometimes an entry in its
  // `bedbankIds` — both shapes exist in the same environment — so try each field in turn.
  const mappedPropertyId = await getPropertyMappingId({ propertyId, allowMysteryRooms: true })
    .catch(() => undefined)
  if (mappedPropertyId) return mappedPropertyId

  return getPropertyMappingId({ bedbankPropertyId: propertyId, allowMysteryRooms: true })
}

async function getPropertyLocation(
  propertyId: string,
  params: PropertyLocationParams,
): Promise<PropertyLocation | undefined> {
  const queryParams: ServerPropertyContentRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    allowMysteryRooms: true,
  }
  const query = qs.stringify(queryParams)
  const encodedPropertyId = encodeURIComponent(propertyId)

  const response = await request.get<ServerPropertyContentResponse>(
    `/api/accommodation/content/properties/${encodedPropertyId}?${query}`,
  )
  return response.result?.location?.coordinates
}

export async function getBedbankPropertyLocation(
  propertyId: string,
  params: PropertyLocationParams,
): Promise<PropertyLocation | undefined> {
  const mappedPropertyId = await getMappedPropertyId(propertyId).catch(() => undefined)
  return getPropertyLocation(mappedPropertyId ?? propertyId, params)
}

function mapPropertyRates({ rooms: serverRooms }: ServerAvailabilityResponseProperty): Array<App.PropertyRoomRates> {
  // NB: Currently, accommodation service gives us _one_ ordering for rooms. Theoretically, the
  // rooms might be ordered differently between hotel-only and hotel-with-flights views. However,
  // this _should_ be rare; we'll cross that bridge when we come to it.
  const sortedServerRooms = sortBy(Object.entries(serverRooms), ([_, { sort }]) => sort, 'asc')
  const rooms = sortedServerRooms.map(([roomId, serverRoom]) => {
    const serverUnavailableReason = serverRoom.unavailableReason
    if (serverUnavailableReason) {
      if (serverUnavailableReason === 'FILTERED_OUT') {
        // We should never get this reason; accommodation service only returns this if the request
        // includes search filters (like `priceLte`). Empirically, however, accommodation service
        // does return this, so we have to pretend it's unavailable for a generic reason.
        // TODO(Xiao): Figure out why.
        return {
          roomId,
          unavailableReason: 'DATE_UNAVAILABLE',
        } as App.UnavailablePropertyRoomRates
      }
      return {
        roomId,
        unavailableReason: serverUnavailableReason,
      }
    }
    const rates = mapObject(serverRoom.ratesMap, serverRate => propertyRateMap(serverRate))
    return {
      roomId,
      hotelOnlyRates: serverRoom.hotelOnlyRates.map(id => rates[id]),
      hotelWithFlightsRates: serverRoom.hotelWithFlightRates.map(id => ({
        ...rates[id],
        includesFlights: true,
      })),
    }
  })
  return rooms.length ? rooms : EmptyArray
}

export async function getPropertyRates(
  propertyId: string,
  params: PropertyRatesParams,
): Promise<Array<App.PropertyRoomRates>> {
  const [highlightedRateId, highlightedRateCurrency] = parseGhaRoomRateId(params.roomRateId)
  const queryParams: ServerUnifiedAvailabilityRequestQueryParams = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    preview: params.previewMode,
    dynamicAvailability: params.dynamicAvailability,
    propertyIds: [propertyId],
    highlightedReason: mapHighlightedReason(params.utmContent),
    highlightedRoomId: params.roomTypeId,
    highlightedRateId,
    highlightedRateCurrency,
    // We'd like to set this so (some LPC) rooms with no rates report _why_ they're unavailable, but
    // we can't due to bugs.
    // TODO(HP-1288): Re-enable when accommodation service bugs are fixed.
    includeUnavailableResults: false,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerUnifiedAvailabilityResponse>(`/api/accommodation/properties/availability?${query}`)
  const serverProperty = response.result.properties[propertyId]
  if (serverProperty) {
    return mapPropertyRates(serverProperty)
  }
  return EmptyArray
}
